import {
    ValidationRuleConfig,
    ValidationResult,
    ValidationError,
    ValidatorOptions,
    CustomRule
} from '../types';
import { allRules } from '../rules';

export class Validator {
    private data: Record<string, any>;
    private options: Required<ValidatorOptions>;
    private customRules: Record<string, CustomRule> = {};

    constructor(data: Record<string, any>, options: ValidatorOptions = {}) {
        this.data = data;
        this.options = {
            language: 'en',
            customMessages: {},
            customRules: {},
            ...options
        };
        this.customRules = this.options.customRules;
    }

    private getNestedValue(path: string): any {
        return path.split('.').reduce((obj, key) => obj?.[key], this.data);
    }

    validateTemplate(template: any[]): ValidationResult {
        const errors: ValidationError[] = [];

        for (const fieldConfig of template) {
            const fieldName = fieldConfig.name;
            const value = this.getNestedValue(fieldName);

            for (const ruleConfig of fieldConfig.validation || []) {
                const error = this.validateRule(fieldName, value, ruleConfig);
                if (error) {
                    errors.push(error);
                }
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            data: this.data
        };
    }

    validateRules(fieldRules: Record<string, ValidationRuleConfig[]>): ValidationResult {
        const errors: ValidationError[] = [];

        for (const [fieldName, rules] of Object.entries(fieldRules)) {
            const value = this.getNestedValue(fieldName);

            for (const ruleConfig of rules) {
                const error = this.validateRule(fieldName, value, ruleConfig);
                if (error) {
                    errors.push(error);
                }
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            data: this.data
        };
    }

    private validateRule(
        fieldName: string,
        value: any,
        ruleConfig: ValidationRuleConfig
    ): ValidationError | null {
        const ruleName = ruleConfig.rule;
        let params = ruleConfig.value !== undefined ? [ruleConfig.value].flat() : [];

        const ruleValidator = allRules[ruleName] || this.customRules[ruleName];
        if (!ruleValidator) {
            throw new Error(`Validation rule '${ruleName}' does not exist.`);
        }

        const result = ruleValidator(value, params, this.data);

        if (result !== true) {
            return {
                field: fieldName,
                rule: ruleName,
                message: this.getMessage(ruleConfig, fieldName, ruleName, params),
                value,
                params: { value: ruleConfig.value }
            };
        }

        return null;
    }

    private getMessage(
        ruleConfig: ValidationRuleConfig,
        fieldName: string,
        ruleName: string,
        params: any[]
    ): string {
        if (ruleConfig.message && ruleConfig.message[this.options.language]) {
            return ruleConfig.message[this.options.language];
        }

        const customMessageKey = `${fieldName}.${ruleName}`;
        if (this.options.customMessages?.[customMessageKey]?.[this.options.language]) {
            return this.options.customMessages[customMessageKey][this.options.language];
        }

        return this.getDefaultMessage(fieldName, ruleName, params);
    }

    private getDefaultMessage(
        fieldName: string,
        ruleName: string,
        params: any[]
    ): string {
        const messages: Record<string, string> = {
            required: `The ${fieldName} field is required.`,
            email: `The ${fieldName} field must be a valid email address.`,
            minLength: `The ${fieldName} field must be at least ${params[0]} characters.`,
            maxLength: `The ${fieldName} field may not be greater than ${params[0]} characters.`,
            length: `The ${fieldName} field must be exactly ${params[0]} characters.`,
            regex: `The ${fieldName} field format is invalid.`,
            url: `The ${fieldName} field must be a valid URL.`,
            ip: `The ${fieldName} field must be a valid IP address.`,
            uuid: `The ${fieldName} field must be a valid UUID.`,
            alpha: `The ${fieldName} field may only contain letters.`,
            alphaNumeric: `The ${fieldName} field may only contain letters and numbers.`,
            alphaDash: `The ${fieldName} field may only contain letters, numbers, dashes and underscores.`,
            startsWith: `The ${fieldName} field must start with "${params[0]}".`,
            endsWith: `The ${fieldName} field must end with "${params[0]}".`,
            contains: `The ${fieldName} field must contain "${params[0]}".`,
            notContains: `The ${fieldName} field must not contain "${params[0]}".`,
            in: `The ${fieldName} field must be one of: ${params.join(', ')}.`,
            notIn: `The ${fieldName} field must not be one of: ${params.join(', ')}.`,
            date: `The ${fieldName} field must be a valid date.`,
            time: `The ${fieldName} field must be a valid time.`,
            json: `The ${fieldName} field must be a valid JSON string.`,
            numeric: `The ${fieldName} field must be a number.`,
            integer: `The ${fieldName} field must be an integer.`,
            float: `The ${fieldName} field must be a float.`,
            min: `The ${fieldName} field must be at least ${params[0]}.`,
            max: `The ${fieldName} field may not be greater than ${params[0]}.`,
            between: `The ${fieldName} field must be between ${params[0]} and ${params[1]}.`,
            positive: `The ${fieldName} field must be positive.`,
            negative: `The ${fieldName} field must be negative.`,
            multipleOf: `The ${fieldName} field must be a multiple of ${params[0]}.`,
            array: `The ${fieldName} field must be an array.`,
            minItems: `The ${fieldName} field must have at least ${params[0]} items.`,
            maxItems: `The ${fieldName} field may not have more than ${params[0]} items.`,
            lengthItems: `The ${fieldName} field must have exactly ${params[0]} items.`,
            includes: `The ${fieldName} field must include ${params[0]}.`,
            excludes: `The ${fieldName} field must not include ${params[0]}.`,
            unique: `The ${fieldName} field must contain unique values.`,
            before: `The ${fieldName} field must be before ${params[0]}.`,
            after: `The ${fieldName} field must be after ${params[0]}.`,
            betweenDates: `The ${fieldName} field must be between ${params[0]} and ${params[1]}.`,
            boolean: `The ${fieldName} field must be a boolean.`,
            accepted: `The ${fieldName} field must be accepted.`,
            requiredIf: `The ${fieldName} field is required when ${params[0]} is ${params[1]}.`,
            requiredUnless: `The ${fieldName} field is required unless ${params[0]} is ${params[1]}.`,
            requiredWith: `The ${fieldName} field is required when ${params[0]} is present.`,
            requiredWithAll: `The ${fieldName} field is required when all of ${params.join(', ')} are present.`,
            requiredWithout: `The ${fieldName} field is required when ${params[0]} is not present.`,
            requiredWithoutAll: `The ${fieldName} field is required when none of ${params.join(', ')} are present.`,
            same: `The ${fieldName} field must match ${params[0]}.`,
            different: `The ${fieldName} field must be different from ${params[0]}.`,
            gt: `The ${fieldName} field must be greater than ${params[0]}.`,
            gte: `The ${fieldName} field must be greater than or equal to ${params[0]}.`,
            lt: `The ${fieldName} field must be less than ${params[0]}.`,
            lte: `The ${fieldName} field must be less than or equal to ${params[0]}.`,
            when: `The ${fieldName} field is invalid.`,
            requiredIfAny: `The ${fieldName} field is required.`,
            requiredIfAll: `The ${fieldName} field is required.`
        };

        return messages[ruleName] || `The ${fieldName} field is invalid.`;
    }

    addCustomRule(name: string, rule: CustomRule): void {
        this.customRules[name] = rule;
    }
}